import { useMemo } from 'react'
import { api } from './index'

export type MenuCategory = {
  ids: string[]
  name: string
  hidden: boolean
}

// Type for processed menu categories with slugs
export type ProcessedMenuCategory = { name: string; slug: string }

export const menuCategoriesApi = api.injectEndpoints({
  endpoints: build => ({
    getMenuCategories: build.query<
      { menu_categories: MenuCategory[] },
      'ALL' | 'HIDDEN'
    >({
      query: state => `/menu_categories?state=${state || 'ALL'}`,
      providesTags: ['MenuCategories'],
    }),
  }),
})

export const { useGetMenuCategoriesQuery } = menuCategoriesApi

/**
 * Converts a category name to a URL-friendly slug
 * @param categoryName The category name to convert
 * @returns A lowercase slug with special characters replaced by hyphens
 */
export function getCategorySlug(categoryName: string): string {
  return categoryName.replace(/[^A-Za-z\d]+/g, '-').toLowerCase()
}

/**
 * Hook to get unique menu categories with slugs
 * Filters out hidden categories and removes duplicates
 * @returns Object with menuCategories array and isLoading boolean
 */
export const useUniqueMenuCategories = (): {
  menuCategories: ProcessedMenuCategory[]
  isLoading: boolean
} => {
  const { data, isLoading } = useGetMenuCategoriesQuery('ALL')
  const menuCategories = useMemo(() => {
    const categories = (data?.menu_categories || [])
      .filter(category => !category.hidden)
      .map(category => category.name)

    const uniqueCategories = Array.from(new Set(categories))
    return uniqueCategories.map(category => ({
      name: category,
      slug: getCategorySlug(category),
    }))
  }, [data])

  return { menuCategories, isLoading }
}
